import { Type, type Static } from '@sinclair/typebox';
import { FileUploadSchema } from '../../shared/file/file-request.schema';
import { PaginationQueryParamsSchema } from '../../shared/utils/pagination-query-params.schema';
import { StringEnum } from '../../utils/typebox';

export const GetPostsListParamsSchema = Type.Object({
  user_id: Type.String({ minLength: 1, description: 'The ID of the User to retrieve posts from.' }),
});

export const GetPostsListQuerySchema = PaginationQueryParamsSchema;

export const PostIdAdditionalDescription =
  "<br>Use IDs from *Get a Post*, *Create a Post* or *List all User's Posts* methods responses only. Some providers may allow native Post IDs, please refer to Posts integration guides.";

export const GetPostParamsSchema = Type.Object({
  post_id: Type.String({
    minLength: 1,
    description: 'The ID of the Post to retrieve.' + PostIdAdditionalDescription,
  }),
});

export const CreatePostBaseBodySchema = Type.Object(
  {
    text: Type.String({
      description:
        'Textual content of the post. User mentions can be added by inserting an @ followed by the ID or public identifier of the user (example: @JohnDoe).',
    }),
    attachments: Type.Optional(Type.Array(FileUploadSchema, { description: 'The list of files to appear in the post.' })),
    can_read: Type.Optional(
      StringEnum(['anyone', 'relations_only'], {
        description: `The visibility level for the new post (if supported by the provider).
          - \`anyone\` is visible to anyone.
          - \`relations_only\` is visible only to the user's relations.`,
        default: 'anyone',
      }),
    ),
    can_comment: Type.Optional(
      StringEnum(['anyone', 'relations_only', 'no_one'], {
        description: `The comment control level for the new post (if supported by the provider).
          - \`anyone\` anyone can comment.
          - \`relations_only\` only to the user's relations can comment.
          - \`no_one\` no one can comment.`,
        default: 'anyone',
      }),
    ),
    quoted_post_id: Type.Optional(
      Type.String({
        minLength: 1,
        description: 'The ID of a Post to be quoted. For a simple repost without commentary, leave `text` as an empty string.',
      }),
    ),
    post_as: Type.Optional(
      Type.String({
        minLength: 1,
        description: 'The ID of the User on whose behalf the post should be published (if supported by the provider).',
      }),
    ),
    specifics: Type.Optional(Type.Any()),
  },
  { additionalProperties: false },
);

export const DeletePostParamsSchema = Type.Object({
  post_id: Type.String({ minLength: 1, description: 'The ID of the Post to delete.' + PostIdAdditionalDescription }),
});

export const GetPostReactionsListQuerySchema = PaginationQueryParamsSchema;

export const GetPostReactionsListParamsSchema = Type.Object({
  post_id: Type.String({
    minLength: 1,
    description: 'The ID of the Post to retrieve reactions from.' + PostIdAdditionalDescription,
  }),
});

export const AddPostReactionParamsSchema = Type.Object({
  post_id: Type.String({ minLength: 1, description: 'The ID of the Post to add the reaction to.' + PostIdAdditionalDescription }),
});

export const RemovePostReactionParamsSchema = Type.Object({
  post_id: Type.String({ minLength: 1, description: 'The ID of the Post where the reaction is.' + PostIdAdditionalDescription }),
});

/**
 * @note Base schema, full schema in provider-shared
 */
export const AddPostReactionBaseBodySchema = Type.Object({
  reaction: Type.String({
    description:
      'The emoji unicode if supported by the provider, else the reaction type (Make sure to use reaction types prefixed by the provider of the targeted account)',
  }),
  react_as: Type.Optional(
    Type.String({
      minLength: 1,
      description: 'The ID of the User on whose behalf the reaction should be published (if supported by the provider).',
    }),
  ),
});

/**
 * @note Base schema, full schema in provider-shared
 */
export const RemovePostReactionBaseBodySchema = Type.Object({
  reaction: Type.String({
    description:
      'The emoji unicode if supported by the provider, else the reaction type (Make sure to use reaction types prefixed by the provider of the targeted account) to be removed',
  }),
  react_as: Type.Optional(
    Type.String({
      minLength: 1,
      description:
        'The ID of the User on whose behalf the reaction was published and should be removed (if supported by the provider).',
    }),
  ),
});

export const UpdatePostBaseSchema = Type.Object(
  {
    text: Type.Optional(
      Type.String({
        description:
          'Textual content of the post. User mentions can be added by inserting an @ followed by the ID or public identifier of the user (example: @JohnDoe).',
      }),
    ),
    can_comment: Type.Optional(
      StringEnum(['anyone', 'relations_only', 'no_one'], {
        description: `The comment control level for the new post (if supported by the provider).
          - \`anyone\` is visible to anyone.
          - \`relations_only\` is visible only to the user's relations.
          - \`no_one\` is visible to no one.`,
        default: 'anyone',
      }),
    ),
  },
  { additionalProperties: false },
);

export const UpdatePostParamsSchema = Type.Object({
  post_id: Type.String({ minLength: 1, description: 'The ID of the Post to update.' + PostIdAdditionalDescription }),
});

export type GetPostsListParams = Static<typeof GetPostsListParamsSchema>;
export type GetPostsListQuery = Static<typeof GetPostsListQuerySchema>;
export type GetPostParams = Static<typeof GetPostParamsSchema>;
export type CreatePostBaseBody<Specifics = object> = Omit<Static<typeof CreatePostBaseBodySchema>, 'specifics'> & {
  specifics?: Specifics;
};
export type UpdatePostBaseBody = Static<typeof UpdatePostBaseSchema>;
export type UpdatePostParams = Static<typeof UpdatePostParamsSchema>;
export type DeletePostParams = Static<typeof DeletePostParamsSchema>;
export type GetPostReactionsListQuery = Static<typeof GetPostReactionsListQuerySchema>;
export type GetPostReactionsListParams = Static<typeof GetPostReactionsListParamsSchema>;
export type AddPostReactionParams = Static<typeof AddPostReactionParamsSchema>;
export type AddPostReactionBaseBody = Static<typeof AddPostReactionBaseBodySchema>;
export type RemovePostReactionParams = Static<typeof RemovePostReactionParamsSchema>;
export type RemovePostReactionBaseBody = Static<typeof RemovePostReactionBaseBodySchema>;
